// Betrieb › Nutzung (h-ui): Monatssummen je Radio, dazu die CSV für die
// Abrechnung. Gemessen wird täglich; bezahlt wird außerhalb der Suite.

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { betriebApi } from "../../api";
import { useAuth } from "../../auth/AuthProvider";
import { errorText } from "../../components/streaming-ui";
import { Meldungen, Sym } from "../../components/social-ui";
import { Button, Card, EmptyState, Page, PageHeader, Skeleton, StatTile } from "../../components/ui";
import { bytesText, zahlText } from "../../components/hosting-ui";
import type { BetriebNutzungZeile } from "../../types";
import { NurBetreiber } from "./NurBetreiber";

function monatJetzt(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function monatVersatz(monat: string, delta: number): string {
  const [j, m] = monat.split("-").map(Number) as [number, number];
  const d = new Date(j, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function monatName(monat: string): string {
  const [j, m] = monat.split("-").map(Number) as [number, number];
  return new Date(j, m - 1, 1).toLocaleDateString("de-DE", { month: "long", year: "numeric" });
}

const stunden = (z: BetriebNutzungZeile): number => z.hoerer_stunden ?? (z.hoerer_sekunden ?? 0) / 3600;

export function NutzungPage(): JSX.Element {
  const { user } = useAuth();
  const [monat, setMonat] = useState(monatJetzt);
  const [zeilen, setZeilen] = useState<BetriebNutzungZeile[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(false);

  useEffect(() => {
    if (user?.role !== "super_admin" || !/^\d{4}-\d{2}$/.test(monat)) return;
    setZeilen(null);
    betriebApi.nutzung(monat).then(setZeilen).catch((err) => { setZeilen([]); setFehler(errorText(err)); });
  }, [monat, user?.role]);

  const summe = useMemo(() => (zeilen ?? []).reduce((s, z) => ({
    stunden: s.stunden + stunden(z),
    spitze: Math.max(s.spitze, z.hoerer_spitze ?? 0),
    traffic: s.traffic + (z.traffic_bytes ?? 0),
    mediathek: s.mediathek + (z.speicher_mediathek_bytes ?? 0),
    bilder: s.bilder + (z.speicher_bilder_bytes ?? 0),
    kanaele: s.kanaele + (z.kanaele ?? 0),
  }), { stunden: 0, spitze: 0, traffic: 0, mediathek: 0, bilder: 0, kanaele: 0 }), [zeilen]);

  if (user?.role !== "super_admin") return <NurBetreiber titel="Nutzung" />;

  const csv = async (): Promise<void> => {
    setLaedt(true);
    try { await betriebApi.nutzungCsv(monat); } catch (err) { setFehler(errorText(err)); } finally { setLaedt(false); }
  };

  return (
    <Page>
      <PageHeader
        eyebrow="Betrieb"
        title="Nutzung"
        description="Was jedes Radio im Monat gebraucht hat. Speicher ist der höchste Stand im Monat, Traffic ist geschätzt (Hörzeit × Bitrate)."
        actions={
          <Button variant="primary" onClick={() => void csv()} disabled={laedt || !zeilen?.length}>
            <Sym name="download" /> {laedt ? "Lädt …" : "CSV herunterladen"}
          </Button>
        }
      />
      <Meldungen error={fehler} />

      <div className="mb-5 flex flex-wrap items-center gap-2">
        <Button onClick={() => setMonat((m) => monatVersatz(m, -1))} aria-label="Vormonat"><Sym name="back" /></Button>
        <input type="month" className="input w-auto" value={monat} max={monatJetzt()} onChange={(e) => e.target.value && setMonat(e.target.value)} aria-label="Monat" />
        <Button onClick={() => setMonat((m) => monatVersatz(m, 1))} disabled={monat >= monatJetzt()} aria-label="Nächster Monat">
          <Sym name="back" className="h-4 w-4 rotate-180" />
        </Button>
        <span className="text-sm text-muted">{monatName(monat)}{monat === monatJetzt() ? " – läuft noch" : ""}</span>
      </div>

      <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile label="Hörerstunden" value={zahlText(summe.stunden)} hint="alle Radios" />
        <StatTile label="Traffic" value={bytesText(summe.traffic)} hint="geschätzt" />
        <StatTile label="Speicher" value={bytesText(summe.mediathek + summe.bilder)} hint="Mediathek + Bilder" />
        <StatTile label="Radios" value={zahlText(zeilen?.length)} />
      </div>

      <Card padded={false} title={`Je Radio · ${monatName(monat)}`}>
        {zeilen === null && <div className="space-y-2 p-5">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-10" />)}</div>}
        {zeilen?.length === 0 && (
          <div className="p-4">
            <EmptyState icon={<Sym name="poll" className="h-5 w-5" />} title="Für diesen Monat gibt es keine Messwerte" description="Die Nutzung wird einmal am Tag erfasst." />
          </div>
        )}
        {zeilen && zeilen.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm">
              <thead>
                <tr className="border-b border-default text-left text-xs text-muted">
                  <th className="px-5 py-2.5 font-medium">Radio</th>
                  <th className="px-3 py-2.5 font-medium">Tarif</th>
                  <th className="px-3 py-2.5 text-right font-medium">Hörerstunden</th>
                  <th className="px-3 py-2.5 text-right font-medium">Hörer-Spitze</th>
                  <th className="px-3 py-2.5 text-right font-medium">Traffic</th>
                  <th className="px-3 py-2.5 text-right font-medium">Mediathek</th>
                  <th className="px-3 py-2.5 text-right font-medium">Bilder</th>
                  <th className="px-5 py-2.5 text-right font-medium">Kanäle</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-default">
                {zeilen.map((z) => (
                  <tr key={z.tenant_id}>
                    <td className="whitespace-nowrap px-5 py-2.5">
                      <Link to={`/betrieb/radios/${z.tenant_id}`} className="font-medium text-primary hover:underline">{z.name}</Link>
                      <div className="text-xs text-muted">{z.slug}</div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-secondary">{z.tarif_name ?? z.tarif ?? "–"}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{zahlText(stunden(z), 1)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{zahlText(z.hoerer_spitze)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{bytesText(z.traffic_bytes)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{bytesText(z.speicher_mediathek_bytes)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{bytesText(z.speicher_bilder_bytes)}</td>
                    <td className="px-5 py-2.5 text-right tabular-nums">{zahlText(z.kanaele)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-default font-semibold text-primary">
                  <td className="px-5 py-2.5" colSpan={2}>Summe</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{zahlText(summe.stunden, 1)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums" title="Höchster Einzelwert">{zahlText(summe.spitze)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{bytesText(summe.traffic)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{bytesText(summe.mediathek)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{bytesText(summe.bilder)}</td>
                  <td className="px-5 py-2.5 text-right tabular-nums">{zahlText(summe.kanaele)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>
    </Page>
  );
}
